import { CartLinesUpdateEvent } from '@shopify/events';

class FurseraBundleRecommendations extends HTMLElement {
  #observer = null;
  #loadPromise = null;
  #abortController = null;

  connectedCallback() {
    if (this.querySelector('fursera-bundle-products')) {
      this.dataset.recommendationsPerformed = 'true';
      return;
    }

    if (this.dataset.recommendationsPerformed === 'true') {
      this.#hideEmptyState();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#load();
      },
      { rootMargin: '700px 0px' },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
  }

  #load() {
    this.#loadPromise ??= this.#loadRecommendations();
    return this.#loadPromise;
  }

  async #loadRecommendations() {
    const configuredIntent = this.dataset.intent || 'complementary';
    const intents = [...new Set([configuredIntent, 'related'])];

    for (const intent of intents) {
      try {
        const rendered = await this.#renderIntent(intent);
        if (rendered) return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    this.dataset.recommendationsPerformed = 'true';
    this.dataset.error = 'No bundle recommendations available';
    this.#hideEmptyState();
  }

  async #renderIntent(intent) {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    if (!productId || !sectionId) return false;

    const url = new URL(this.dataset.url || '/recommendations/products?limit=6', window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', intent);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const timeout = window.setTimeout(() => this.#abortController?.abort(), 5000);

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return false;

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const rendered = documentFragment.querySelector('fursera-bundle-recommendations');
      if (!rendered?.querySelector('fursera-bundle-products')) return false;

      this.replaceChildren(...rendered.cloneNode(true).childNodes);
      this.hidden = false;
      delete this.dataset.error;
      this.dataset.recommendationsPerformed = 'true';
      this.dataset.activeIntent = intent;
      return true;
    } finally {
      window.clearTimeout(timeout);
      this.#abortController = null;
    }
  }

  #hideEmptyState() {
    this.hidden = true;
    this.replaceChildren();
  }
}

class FurseraBundleProducts extends HTMLElement {
  #items = [];
  #selectedItems = [];
  #addButton = null;
  #status = null;
  #totalElement = null;
  #itemsContainer = null;
  #moneyFormatter = null;
  #seenProductIds = new Set();
  #recommendationsPromise = null;
  #recommendationObserver = null;
  #isAdding = false;

  connectedCallback() {
    this.#items = [...this.querySelectorAll('[data-bundle-item]')];
    this.#addButton = this.querySelector('[data-bundle-add]');
    this.#status = this.querySelector('[data-bundle-status]');
    this.#totalElement = this.querySelector('[data-bundle-total]');
    this.#itemsContainer = this.querySelector('[data-bundle-items]');
    this.#rememberVisibleProducts();
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('pointerenter', this.#warmRecommendations, { once: true });
    this.addEventListener('focusin', this.#warmRecommendations, { once: true });
    this.#addButton?.addEventListener('click', this.#addSelected);
    this.#observeForRecommendations();
    this.#updateSummary();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('pointerenter', this.#warmRecommendations);
    this.removeEventListener('focusin', this.#warmRecommendations);
    this.#addButton?.removeEventListener('click', this.#addSelected);
    this.#recommendationObserver?.disconnect();
  }

  #handleChange = (event) => {
    const select = event.target.closest?.('[data-bundle-variant]');
    if (select instanceof HTMLSelectElement) {
      const item = select.closest('[data-bundle-item]');
      const option = select.selectedOptions[0];
      if (item instanceof HTMLElement && option) {
        item.dataset.variantId = option.value;
        item.dataset.price = option.dataset.price || '0';
        const price = item.querySelector('[data-item-price]');
        if (price) price.textContent = this.#money(Number(item.dataset.price));

        const image = item.querySelector('.fursera-bundle-products__image');
        if (image instanceof HTMLImageElement && option.dataset.image) {
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
        }
      }
    }

    this.#updateSummary();
  };

  #updateSummary() {
    this.#selectedItems = [];
    let total = 0;

    for (const item of this.#items) {
      const checkbox = item.querySelector('[data-bundle-checkbox]');
      const selected = checkbox instanceof HTMLInputElement && checkbox.checked && Boolean(item.dataset.variantId);
      item.dataset.selected = String(selected);
      if (selected) {
        this.#selectedItems.push(item);
        total += Number(item.dataset.price || 0);
      }
    }

    if (this.#totalElement) this.#totalElement.textContent = this.#money(total);
    if (this.#addButton instanceof HTMLButtonElement) {
      this.#addButton.disabled = this.#isAdding || this.#selectedItems.length === 0;
      this.#addButton.textContent = this.#buttonLabel(this.#selectedItems.length);
    }
  }

  #buttonLabel(count) {
    if (count === 0) return this.dataset.chooseLabel || this.dataset.addLabel || 'Add to cart';
    if (count === 1) return this.dataset.addOneLabel || this.dataset.addLabel || 'Add to cart';
    return (this.dataset.addAllLabel || this.dataset.addLabel || 'Add to cart').replace('{{ count }}', String(count));
  }

  #money(cents) {
    try {
      this.#moneyFormatter ??= new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });
      return this.#moneyFormatter.format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${this.dataset.currency || ''}`.trim();
    }
  }

  #rememberVisibleProducts() {
    if (this.dataset.productId) this.#seenProductIds.add(String(this.dataset.productId));
    for (const item of this.#items) {
      if (item.dataset.productId) this.#seenProductIds.add(String(item.dataset.productId));
    }
  }

  #imageUrl(source, width = 420) {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.href;
    } catch {
      return source;
    }
  }

  #productCard(product) {
    const variants = (product.variants || []).filter((variant) => variant.available);
    const variant = variants[0];
    if (!variant) return null;

    const article = document.createElement('article');
    article.className = 'fursera-bundle-products__item';
    article.dataset.bundleItem = '';
    article.dataset.productId = String(product.id);
    article.dataset.variantId = String(variant.id);
    article.dataset.price = String(variant.price || 0);

    const checkLabel = document.createElement('label');
    checkLabel.className = 'fursera-bundle-products__check';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = true;
    checkbox.dataset.bundleCheckbox = '';
    const hiddenTitle = document.createElement('span');
    hiddenTitle.className = 'visually-hidden';
    hiddenTitle.textContent = product.title;
    checkLabel.append(checkbox, hiddenTitle);

    const imageLink = document.createElement('a');
    imageLink.className = 'fursera-bundle-products__image-link';
    imageLink.href = product.url;
    const imageSource = variant.featured_image?.src || product.featured_image;
    if (imageSource) {
      const image = document.createElement('img');
      image.className = 'fursera-bundle-products__image';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.alt = product.title;
      image.src = this.#imageUrl(imageSource);
      image.srcset = [160, 240, 320, 420]
        .map((width) => `${this.#imageUrl(imageSource, width)} ${width}w`)
        .join(', ');
      image.sizes = '(max-width: 749px) 30vw, 190px';
      imageLink.append(image);
    }

    const copy = document.createElement('div');
    copy.className = 'fursera-bundle-products__item-copy';
    const title = document.createElement('a');
    title.className = 'fursera-bundle-products__title';
    title.href = product.url;
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'fursera-bundle-products__price';
    price.dataset.itemPrice = '';
    price.textContent = this.#money(Number(variant.price || 0));
    copy.append(title, price);

    const hasVariantOptions = variants.length > 1 || (variant.public_title && variant.title !== 'Default Title');
    if (hasVariantOptions) {
      const variantLabel = document.createElement('label');
      variantLabel.className = 'fursera-bundle-products__variant-label';
      const labelText = document.createElement('span');
      labelText.textContent = this.dataset.optionsLabel || 'Options';
      const select = document.createElement('select');
      select.dataset.bundleVariant = '';

      for (const availableVariant of variants) {
        const option = document.createElement('option');
        option.value = String(availableVariant.id);
        option.dataset.price = String(availableVariant.price || 0);
        const variantImage = availableVariant.featured_image?.src;
        if (variantImage) option.dataset.image = this.#imageUrl(variantImage);
        option.textContent = `${availableVariant.title} — ${this.#money(Number(availableVariant.price || 0))}`;
        select.append(option);
      }

      variantLabel.append(labelText, select);
      copy.append(variantLabel);
    }

    article.append(checkLabel, imageLink, copy);
    return article;
  }

  #warmRecommendations = () => {
    this.#recommendationProducts().catch(() => {});
  };

  #observeForRecommendations() {
    if (!('IntersectionObserver' in window)) return;
    this.#recommendationObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#recommendationObserver?.disconnect();
        this.#recommendationObserver = null;
        this.#warmRecommendations();
      },
      { rootMargin: '700px 0px' },
    );
    this.#recommendationObserver.observe(this);
  }

  #recommendationProducts() {
    if (!this.#recommendationsPromise) {
      this.#recommendationsPromise = this.#loadRecommendationProducts().then((products) => {
        if (!products.length) this.#recommendationsPromise = null;
        return products;
      });
    }
    return this.#recommendationsPromise;
  }

  async #loadRecommendationProducts() {
    const root = window.Shopify?.routes?.root || '/';
    const productId = this.dataset.productId;
    if (!productId) return [];

    const requests = ['complementary', 'related'].map(async (intent) => {
      const url = new URL(`${root}recommendations/products.json`, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', intent);
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);
      return (await response.json()).products || [];
    });

    const results = await Promise.allSettled(requests);
    const productsById = new Map();
    for (const result of results) {
      if (result.status !== 'fulfilled') continue;
      for (const product of result.value) {
        if (!productsById.has(String(product.id))) productsById.set(String(product.id), product);
      }
    }
    return [...productsById.values()];
  }

  #replaceItems(itemsToReplace, replacementCards = []) {
    const container = this.#itemsContainer;
    if (!(container instanceof HTMLElement)) return false;

    const replaceSet = new Set(itemsToReplace);
    const nextItems = [];
    let replacementIndex = 0;

    for (const item of this.#items) {
      if (replaceSet.has(item)) {
        const replacement = replacementCards[replacementIndex];
        replacementIndex += 1;
        if (replacement) nextItems.push(replacement);
      } else {
        nextItems.push(item);
      }
    }

    if (!nextItems.length) return false;
    const fragment = document.createDocumentFragment();
    nextItems.forEach((item, index) => {
      if (index > 0) {
        const plus = document.createElement('span');
        plus.className = 'fursera-bundle-products__plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        fragment.append(plus);
      }
      fragment.append(item);
    });

    container.replaceChildren(fragment);
    this.#items = nextItems;
    this.#updateSummary();
    return true;
  }

  async #refreshProducts(itemsToReplace, recommendationProducts) {
    if (!itemsToReplace.length) return false;

    const currentIds = new Set(
      this.#items.map((item) => String(item.dataset.productId || '')).filter(Boolean),
    );
    const sourceProductId = String(this.dataset.productId || '');
    const products = recommendationProducts.filter((product) => {
      const productId = String(product.id);
      return (
        product.available &&
        productId !== sourceProductId &&
        !currentIds.has(productId) &&
        (product.variants || []).some((variant) => variant.available)
      );
    });

    const freshProducts = products.filter((product) => !this.#seenProductIds.has(String(product.id)));
    const recycledProducts = products.filter((product) => this.#seenProductIds.has(String(product.id)));
    const nextProducts = [...freshProducts, ...recycledProducts].slice(0, itemsToReplace.length);
    const replacementCards = nextProducts.map((product) => this.#productCard(product)).filter(Boolean);
    nextProducts.forEach((product) => {
      this.#seenProductIds.add(String(product.id));
    });
    return this.#replaceItems(itemsToReplace, replacementCards);
  }

  #hideRecommendations() {
    const recommendations = this.closest('product-recommendations');
    if (recommendations instanceof HTMLElement) recommendations.hidden = true;
  }

  #addSelected = async () => {
    const button = this.#addButton;
    const status = this.#status;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const groupedItems = new Map();
    for (const item of this.#selectedItems) {
      const variantId = String(item.dataset.variantId || '');
      if (!variantId) continue;
      groupedItems.set(variantId, (groupedItems.get(variantId) || 0) + 1);
    }
    const items = [...groupedItems].map(([id, quantity]) => ({ id: Number(id), quantity }));
    if (!items.length) return;
    const itemsToReplace = [...this.#selectedItems];
    const recommendationProductsPromise = this.#recommendationProducts().catch(() => []);

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      }),
    );

    this.#isAdding = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.addingLabel || 'Adding…';
    if (status) status.hidden = true;

    try {
      const response = await fetch(this.dataset.cartAddUrl || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.errorLabel || 'Unable to add products.');
      }

      const cartPromise = fetch(this.dataset.cartUrl || `${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      }).then((cartResponse) => cartResponse.json());

      button.textContent = this.dataset.addedLabel || 'Added to cart';
      if (status) {
        status.textContent = this.dataset.addedLabel || 'Added to cart';
        status.hidden = false;
      }

      this.setAttribute('aria-busy', 'true');
      let refreshed = false;
      try {
        const recommendationProducts = await recommendationProductsPromise;
        refreshed = await this.#refreshProducts(itemsToReplace, recommendationProducts);
      } catch {
        refreshed = this.#replaceItems(itemsToReplace);
      }
      this.removeAttribute('aria-busy');
      button.removeAttribute('aria-busy');

      if (refreshed) {
        if (status) status.hidden = true;
        this.#updateSummary();
      } else {
        window.setTimeout(() => this.#hideRecommendations(), 700);
      }

      const cart = await cartPromise;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'associated-bundle',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      this.#isAdding = false;
      this.#updateSummary();
    } catch (error) {
      deferred.reject(error);
      this.#isAdding = false;
      button.removeAttribute('aria-busy');
      if (status) {
        status.textContent = error instanceof Error ? error.message : this.dataset.errorLabel;
        status.hidden = false;
      }
      this.#updateSummary();
    }
  };
}

if (!customElements.get('fursera-bundle-products')) {
  customElements.define('fursera-bundle-products', FurseraBundleProducts);
}

if (!customElements.get('fursera-bundle-recommendations')) {
  customElements.define('fursera-bundle-recommendations', FurseraBundleRecommendations);
}
